import Vue from 'vue';
import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import {
  buildColumnQuery,
  getGroupValue,
  MAX_KANBAN_COLUMNS,
} from 'dashboard/components/widgets/conversation/kanban/helpers';

// Max parallel `/filter` requests per board load. The board fetches one request
// per column; capping concurrency keeps a wide custom-attribute board from
// hammering the API with a burst of expensive filter queries at once.
const FETCH_CONCURRENCY = 4;

const emptyColumn = () => ({
  ids: [],
  page: 0,
  total: 0,
  hasMore: false,
  loading: false,
});

const state = {
  // Grouping config the columns were fetched for: { type, key }.
  groupBy: { type: 'status' },
  // Snapshot of the board's active global filters (ChatList.conversationFilters).
  baseFilters: {},
  // Per-column pagination metadata keyed by column id. Records themselves live
  // in the canonical `conversations` store so websocket updates keep flowing.
  columns: {},
  // Bumped on every setup. In-flight fetches capture it and drop their result
  // if it changed meanwhile, so a slow response from a previous filter/grouping
  // can never overwrite the columns of a newer one.
  generation: 0,
};

export const getters = {
  // Index of the canonical conversation list, rebuilt only when it changes
  // (Vuex caches this) so hydrating N columns per render stays O(N) lookups
  // instead of rebuilding the Map once per column.
  conversationsById: (_state, _getters, rootState) =>
    new Map(
      (rootState.conversations.allConversations || []).map(chat => [
        chat.id,
        chat,
      ])
    ),

  // Hydrate a column's stored ids against the canonical conversation list.
  getColumn: ($state, $getters) => columnId => {
    const column = $state.columns[columnId] || emptyColumn();
    const byId = $getters.conversationsById;
    const conversations = column.ids
      .map(id => byId.get(id))
      .filter(Boolean)
      // Membership guard: a card whose grouping value changed via a websocket
      // update (e.g. pending -> resolved) leaves its old column immediately
      // instead of lingering with a stale position until the next refetch.
      .filter(chat => getGroupValue(chat, $state.groupBy) === columnId);
    return {
      conversations,
      total: column.total,
      hasMore: column.hasMore,
      loading: column.loading,
    };
  },
};

export const actions = {
  // Reset the board for a new grouping/filter context and fetch page 1 of every
  // column with bounded concurrency.
  setup: async ({ commit, dispatch }, { groupBy, columns, baseFilters }) => {
    commit(types.SET_KANBAN_CONTEXT, { groupBy, baseFilters });

    const limited = columns.slice(0, MAX_KANBAN_COLUMNS);
    if (columns.length > MAX_KANBAN_COLUMNS) {
      // Never truncate coverage silently — the dropped columns must be visible.
      // eslint-disable-next-line no-console
      console.warn(
        `[kanban] ${columns.length} columns requested, fetching first ` +
          `${MAX_KANBAN_COLUMNS}; ${columns.length - MAX_KANBAN_COLUMNS} ` +
          `column(s) were not loaded.`
      );
    }

    commit(
      types.RESET_KANBAN_COLUMNS,
      limited.map(column => column.id)
    );

    const queue = [...limited];
    const worker = async () => {
      const column = queue.shift();
      if (!column) return;
      await dispatch('fetchColumn', { columnId: column.id, page: 1 });
      await worker();
    };
    await Promise.all(
      Array.from({ length: Math.min(FETCH_CONCURRENCY, queue.length) }, worker)
    );
  },

  // Fetch one page of a single column via the advanced filter endpoint and merge
  // the records into the canonical conversation store.
  fetchColumn: async (
    { commit, dispatch, state: $state, rootGetters },
    { columnId, page }
  ) => {
    // Capture the context this fetch belongs to; if a newer setup bumps the
    // generation while we await, the response is stale and must be dropped.
    const generation = $state.generation;
    const currentUserId = rootGetters.getCurrentUserID;
    // On the "me" tab the column is scoped to assignee_id = current user.
    // Without that id the request would be unscoped and leak other agents'
    // conversations, so skip the fetch until the user is known (a re-setup
    // fires once currentUserId lands — see KanbanView.setupKey).
    if ($state.baseFilters.assigneeType === 'me' && !currentUserId) {
      commit(types.SET_KANBAN_COLUMN_LOADING, { columnId, loading: false });
      return;
    }
    commit(types.SET_KANBAN_COLUMN_LOADING, { columnId, loading: true });
    try {
      const { queryData, sortBy } = buildColumnQuery({
        columnId,
        groupBy: $state.groupBy,
        baseFilters: $state.baseFilters,
        currentUserId,
      });
      const { data } = await ConversationApi.filter({
        queryData,
        page,
        sortBy,
        // Board only renders all_count per column; skip the assignee counts.
        leanCount: true,
      });
      if ($state.generation !== generation) return;
      const { payload: conversationList = [], meta = {} } = data;

      // Reuse the canonical upsert so records stay de-duplicated and reactive to
      // websocket events, exactly like the list view. The `conversations` module
      // is NOT namespaced, so commit the mutation at the root with no prefix.
      commit(types.SET_ALL_CONVERSATION, conversationList, { root: true });
      dispatch(
        'conversationLabels/setBulkConversationLabels',
        conversationList,
        { root: true }
      );

      commit(types.APPEND_KANBAN_COLUMN_PAGE, {
        columnId,
        page,
        ids: conversationList.map(chat => chat.id),
        total: meta.all_count ?? 0,
      });
    } catch (error) {
      if ($state.generation !== generation) return;
      commit(types.SET_KANBAN_COLUMN_LOADING, { columnId, loading: false });
    }
  },

  loadMoreColumn: async ({ dispatch, state: $state }, { columnId }) => {
    const column = $state.columns[columnId];
    if (!column || column.loading || !column.hasMore) return;
    await dispatch('fetchColumn', { columnId, page: column.page + 1 });
  },

  reset: ({ commit }) => commit(types.RESET_KANBAN),
};

export const mutations = {
  [types.SET_KANBAN_CONTEXT]($state, { groupBy, baseFilters }) {
    $state.groupBy = groupBy;
    $state.baseFilters = baseFilters;
    // Invalidate any fetch still in flight for the previous context.
    $state.generation += 1;
  },

  [types.RESET_KANBAN_COLUMNS]($state, columnIds) {
    const columns = {};
    columnIds.forEach(id => {
      columns[id] = emptyColumn();
    });
    Vue.set($state, 'columns', columns);
  },

  [types.SET_KANBAN_COLUMN_LOADING]($state, { columnId, loading }) {
    const column = $state.columns[columnId];
    if (column) Vue.set(column, 'loading', loading);
  },

  [types.APPEND_KANBAN_COLUMN_PAGE]($state, { columnId, page, ids, total }) {
    const column = $state.columns[columnId] || emptyColumn();
    // Page 1 replaces (fresh load); later pages append. De-dup defensively in
    // case an id shifts pages between requests.
    const merged = page <= 1 ? [] : [...column.ids];
    ids.forEach(id => {
      if (!merged.includes(id)) merged.push(id);
    });
    Vue.set($state.columns, columnId, {
      ids: merged,
      page,
      total,
      hasMore: merged.length < total,
      loading: false,
    });
  },

  [types.RESET_KANBAN]($state) {
    Vue.set($state, 'columns', {});
    $state.baseFilters = {};
    $state.groupBy = { type: 'status' };
    // Drop any fetch still in flight so it can't re-create a column post-reset.
    $state.generation += 1;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
